import { useEffect, useRef, useState } from 'react';
import {
  DropDown,
  Flex,
  FlexColumn,
  FormControls,
  ToggleButton,
} from '@elementary/common-ui/components';
import { AnomalySensitivity, Period } from '@elementary/common-ui/types';
import { ToggleButtonGroup } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { anomalyDirectionOptions } from 'shared/constants';
import { AddTestForm } from 'shared/types';
import TimeWithUnitField from 'shared/components/TimeWithUnitField';
import {
  pipelineThresholdUnitOptions,
  sensitivityOptions,
} from 'shared/constants';
import {
  DEFAULT_ANOMALY_TRAINING_PERIOD,
  isUnsetPeriodCount,
} from '../constants';
import SeveritySelector from '../SeveritySelector';
import TestMetadataForm from './shared/TestMetadataForm';
import WizardTrainingPeriodSection from './shared/WizardTrainingPeriodSection';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  skipDefaults?: boolean;
};

const TablesPipelineTaskPerformanceCloud = ({
  methods,
  skipDefaults,
}: Props) => {
  const [expanded, setExpanded] = useState(true);
  const lastThresholdRef = useRef<Period | null>(null);
  const mode =
    methods.watch('anomalyConfig.fixedThreshold') == null
      ? 'anomaly'
      : 'threshold';

  useEffect(() => {
    if (skipDefaults) return;
    if (methods.getValues('anomalyConfig.anomalyDirection') == null) {
      methods.setValue('anomalyConfig.anomalyDirection', 'SPIKE');
    }
    if (methods.getValues('anomalyConfig.sensitivity') == null) {
      methods.setValue(
        'anomalyConfig.sensitivity',
        sensitivityOptions[1].value as AnomalySensitivity,
      );
    }
    if (
      isUnsetPeriodCount(
        methods.getValues('anomalyConfig.trainingPeriod.count'),
      )
    ) {
      methods.setValue(
        'anomalyConfig.trainingPeriod.count',
        DEFAULT_ANOMALY_TRAINING_PERIOD.count,
      );
      methods.setValue(
        'anomalyConfig.trainingPeriod.period',
        DEFAULT_ANOMALY_TRAINING_PERIOD.period,
      );
    }
    if (methods.getValues('anomalyConfig.fixedThreshold') === undefined) {
      methods.setValue('anomalyConfig.fixedThreshold', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipDefaults]);

  return (
    <FormControls.TestAccordion
      summary="Pipeline task performance"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <FlexColumn sx={{ gap: 2 }}>
            <FormControls.FormTitle>Detection mode</FormControls.FormTitle>
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={(_, val) => {
                if (!val) return;
                if (val === 'anomaly') {
                  const current = methods.getValues(
                    'anomalyConfig.fixedThreshold',
                  );
                  if (current != null) {
                    lastThresholdRef.current = current as Period;
                  }
                  methods.setValue('anomalyConfig.fixedThreshold', null, {
                    shouldDirty: true,
                  });
                } else {
                  methods.setValue(
                    'anomalyConfig.fixedThreshold',
                    lastThresholdRef.current ?? { count: 1, period: 'hour' },
                    { shouldDirty: true },
                  );
                }
              }}
            >
              <ToggleButton
                value="anomaly"
                size="large"
                sx={{ width: '300px' }}
              >
                Anomaly detection
              </ToggleButton>
              <ToggleButton
                value="threshold"
                size="large"
                sx={{ width: '300px' }}
              >
                Static threshold
              </ToggleButton>
            </ToggleButtonGroup>
          </FlexColumn>
        </FormControls.FieldsWrapper>

        <FlexColumn
          sx={{ gap: 3, display: mode === 'anomaly' ? 'flex' : 'none' }}
        >
          <FormControls.FieldsWrapper style={{ gap: '8px' }}>
            <FormControls.FormTitle>Anomaly direction</FormControls.FormTitle>
            <FormControls.Row style={{ gap: '8px', flexWrap: 'wrap' }}>
              <FormControls.FormText>Detect</FormControls.FormText>
              <Controller
                control={methods.control}
                name="anomalyConfig.anomalyDirection"
                render={({ field }) => (
                  <DropDown
                    sortOptions={false}
                    options={anomalyDirectionOptions}
                    sx={{ minWidth: '180px' }}
                    {...field}
                  />
                )}
              />
              <FormControls.FormText>
                {`in the task's run duration.`}
              </FormControls.FormText>
            </FormControls.Row>
          </FormControls.FieldsWrapper>

          <FormControls.FieldsWrapper style={{ gap: '8px' }}>
            <FormControls.FormTitle>Sensitivity</FormControls.FormTitle>
            <FormControls.Row style={{ gap: '8px', flexWrap: 'wrap' }}>
              <FormControls.FormText>
                Adjust test thresholds by setting the sensitivity level to be
              </FormControls.FormText>
              <Controller
                control={methods.control}
                name="anomalyConfig.sensitivity"
                render={({ field }) => (
                  <DropDown
                    sortOptions={false}
                    options={sensitivityOptions}
                    {...field}
                  />
                )}
              />
            </FormControls.Row>
          </FormControls.FieldsWrapper>
        </FlexColumn>

        {mode === 'threshold' && (
          <FormControls.FieldsWrapper style={{ gap: '8px' }}>
            <FormControls.FormTitle>Threshold</FormControls.FormTitle>
            <Flex sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControls.FormText>
                Alert when run duration exceeds
              </FormControls.FormText>
              <Controller
                control={methods.control}
                name="anomalyConfig.fixedThreshold"
                render={({ field }) => (
                  <TimeWithUnitField
                    onChange={field.onChange}
                    count={methods.watch('anomalyConfig.fixedThreshold.count')}
                    period={methods.watch(
                      'anomalyConfig.fixedThreshold.period',
                    )}
                    options={pipelineThresholdUnitOptions}
                    defaultPeriod="hour"
                    disabled={false}
                  />
                )}
              />
            </Flex>
          </FormControls.FieldsWrapper>
        )}

        <WizardTrainingPeriodSection methods={methods} />

        <FormControls.FieldsWrapper>
          <Controller
            name="severity"
            control={methods.control}
            defaultValue={{ type: 'warn' }}
            render={({ field }) => <SeveritySelector {...field} />}
          />
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesPipelineTaskPerformanceCloud;
